/** Player sprite assets and frame layout */

import { assetUrl } from "../assetUrl";

export const FRAME_W = 54;
export const FRAME_H = 84;
/** Y offset from fighter center down to the feet (pace aim / indicator). */
export const FEET_OFFSET_Y = FRAME_H / 2 - 2;
export const STEP_FRAMES = 4;
export const DANCE_FRAMES = 4;
/** ms per dance frame while looping the victory anim. */
export const DANCE_FRAME_MS = 120;
/** Laid-down corpse sprite (wider than tall). */
export const DEAD_W = 84;
export const DEAD_H = 54;
/** Arm top-left anchors here on the aiming body (sprite local px). */
export const ARM_ANCHOR = { x: 33, y: 30 };

export type SpriteKey =
  | "idle"
  | "step1"
  | "step2"
  | "aiming"
  | "arm"
  | "armShotgun"
  | "dance"
  | "dead"
  | "enemyIdle"
  | "enemyStep1"
  | "enemyStep2"
  | "enemyAiming"
  | "enemyArm"
  | "enemyArmShotgun"
  | "enemyDance"
  | "cactus1"
  | "cactus2"
  | "cactus3"
  | "rocks1"
  | "rocks2"
  | "rocks3"
  | "rocks4"
  | "rocks5"
  | "sandDunes1"
  | "sandDunes2"
  | "sandDunes3"
  | "sandDunes4"
  | "sandDunes5"
  | "dust"
  | "smoke"
  | "cactusSuit"
  | "enemyCactusSuit"
  | "dummy"
  | "enemyDummy"
  | "tumbleweed"
  | "jackrabbit"
  | "horse"
  | "snake"
  | "bg"
  | "bgCloud";

const PATHS: Record<SpriteKey, string> = {
  idle: assetUrl("sprites/player_idle.png"),
  step1: assetUrl("sprites/player_step1.png"),
  step2: assetUrl("sprites/player_step2.png"),
  aiming: assetUrl("sprites/player_aiming.png"),
  arm: assetUrl("sprites/player_arm.png"),
  armShotgun: assetUrl("sprites/player_arm_shotgun.png"),
  dance: assetUrl("sprites/player_dance.png"),
  dead: assetUrl("sprites/player_dead.png"),
  enemyIdle: assetUrl("sprites/enemy_player_idle.png"),
  enemyStep1: assetUrl("sprites/enemy_player_step1.png"),
  enemyStep2: assetUrl("sprites/enemy_player_step2.png"),
  enemyAiming: assetUrl("sprites/enemy_player_aiming.png"),
  enemyArm: assetUrl("sprites/enemy_player_arm.png"),
  enemyArmShotgun: assetUrl("sprites/enemy_player_arm_shotgun.png"),
  enemyDance: assetUrl("sprites/enemy_player_dance.png"),
  cactus1: assetUrl("sprites/cactus1.png"),
  cactus2: assetUrl("sprites/cactus2.png"),
  cactus3: assetUrl("sprites/cactus3.png"),
  rocks1: assetUrl("sprites/rocks1.png"),
  rocks2: assetUrl("sprites/rocks2.png"),
  rocks3: assetUrl("sprites/rocks3.png"),
  rocks4: assetUrl("sprites/rocks4.png"),
  rocks5: assetUrl("sprites/rocks5.png"),
  sandDunes1: assetUrl("sprites/sand_dunes1.png"),
  sandDunes2: assetUrl("sprites/sand_dunes2.png"),
  sandDunes3: assetUrl("sprites/sand_dunes3.png"),
  sandDunes4: assetUrl("sprites/sand_dunes4.png"),
  sandDunes5: assetUrl("sprites/sand_dunes5.png"),
  dust: assetUrl("sprites/dust_particle.png"),
  smoke: assetUrl("sprites/smoke_particle.png"),
  cactusSuit: assetUrl("sprites/player_cactus.png"),
  enemyCactusSuit: assetUrl("sprites/enemy_player_cactus.png"),
  dummy: assetUrl("sprites/player_dummy.png"),
  enemyDummy: assetUrl("sprites/enemy_player_dummy.png"),
  tumbleweed: assetUrl("sprites/tumbleweed.png"),
  jackrabbit: assetUrl("sprites/jackrabbit_hop.png"),
  horse: assetUrl("sprites/horse.png"),
  snake: assetUrl("sprites/snake.png"),
  bg: assetUrl("sprites/bg.png"),
  bgCloud: assetUrl("sprites/bg_clouds.png"),
};

const images: Partial<Record<SpriteKey, HTMLImageElement>> = {};
let loadPromise: Promise<void> | null = null;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}

/** Prefetch all player sprites. Safe to call multiple times. */
export function loadPlayerSprites(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    const entries = Object.entries(PATHS) as [SpriteKey, string][];
    await Promise.all(
      entries.map(async ([key, src]) => {
        try {
          images[key] = await loadImage(src);
        } catch (err) {
          console.warn(err);
        }
      })
    );
  })();
  return loadPromise;
}

export function getSprite(key: SpriteKey): HTMLImageElement | null {
  return images[key] ?? null;
}

/** Ally (local) vs enemy (remote) sprite variants. */
export function getFighterSprite(
  isLocal: boolean,
  part: "idle" | "step1" | "step2" | "aiming" | "arm" | "dance"
): HTMLImageElement | null {
  if (isLocal) return getSprite(part);
  if (part === "idle") return getSprite("enemyIdle");
  if (part === "step1") return getSprite("enemyStep1");
  if (part === "step2") return getSprite("enemyStep2");
  if (part === "aiming") return getSprite("enemyAiming");
  if (part === "arm") return getSprite("enemyArm");
  return getSprite("enemyDance");
}

/** Revolver or sawed-off arm for the aiming pose. */
export function getArmSprite(
  isLocal: boolean,
  shotgun: boolean
): HTMLImageElement | null {
  if (shotgun) {
    return getSprite(isLocal ? "armShotgun" : "enemyArmShotgun");
  }
  return getFighterSprite(isLocal, "arm");
}

export function getDecorSprite(
  type: "cactus" | "rocks" | "sandDunes" | "horse" | "dead",
  variant: number
): HTMLImageElement | null {
  if (type === "horse") return getSprite("horse");
  if (type === "dead") return getSprite("dead");
  return getSprite(`${type}${variant}` as SpriteKey);
}

export function spritesReady(): boolean {
  return (
    !!images.idle &&
    !!images.step1 &&
    !!images.step2 &&
    !!images.aiming &&
    !!images.arm &&
    !!images.armShotgun &&
    !!images.dance &&
    !!images.dead &&
    !!images.enemyIdle &&
    !!images.enemyStep1 &&
    !!images.enemyStep2 &&
    !!images.enemyAiming &&
    !!images.enemyArm &&
    !!images.enemyArmShotgun &&
    !!images.enemyDance
  );
}
